import { useMemo } from 'react';
import type { ApprovalRequest } from '@/lib/types';
import { isAlreadyInvoiced, isStaleCandidate, isUnapprovedCandidate } from '../grnCandidates';
import { BucketShell } from './_BucketShell';
import { readStructuredFields } from './_payload';

export interface MultipleGrnsViewProps {
  req: ApprovalRequest;
}

function readRecommended(req: ApprovalRequest): string[] {
  const sf = readStructuredFields(req);
  const recommended = sf['recommended_grn_nos'];
  if (!Array.isArray(recommended)) return [];
  return recommended.map((v) => String(v));
}

function readCandidates(req: ApprovalRequest): Record<string, unknown>[] {
  const sf = readStructuredFields(req);
  const candidates = [
    sf['postable_grn_candidates'],
    sf['unapproved_grn_candidates'],
    sf['recommended_grn_candidates'],
    sf['grn_candidates'],
  ];
  return candidates.flatMap((value) =>
    Array.isArray(value)
      ? value.filter((item): item is Record<string, unknown> => {
          return !!item && typeof item === 'object' && !Array.isArray(item);
        })
      : [],
  );
}

export function MultipleGrnsView({ req }: MultipleGrnsViewProps) {
  const recommended = readRecommended(req);
  const candidates = readCandidates(req);
  const allCandidatesBlocked =
    candidates.length > 0 &&
    candidates.every(
      (candidate) =>
        isAlreadyInvoiced(candidate) ||
        isStaleCandidate(candidate) ||
        isUnapprovedCandidate(candidate),
    );

  const headline = useMemo(() => {
    if (allCandidatesBlocked) {
      return 'All visible GRN candidates are blocked by live Oracle evidence.';
    }
    if (recommended.length === 0) return 'Multiple GRN candidates returned by Oracle.';
    return `Recommended GRNs: ${recommended.join(', ')}.`;
  }, [allCandidatesBlocked, recommended]);

  // The GRN picker itself lives in ReprocessForm — rendering it here would
  // surface two pickers on the same row and confuse Finance about which one
  // drives the submit. The bucket view only shows the classification banner;
  // the form below owns the picker, the manual-entry toggle, and submit.
  return (
    <BucketShell req={req}>
      <section className="rounded-lg border border-orange-300 bg-orange-50 p-16">
        <p className="text-xs font-semibold uppercase tracking-wide text-orange-800">
          Multiple GRNs
        </p>
        <p className="mt-4 text-sm text-neutral-700">{headline}</p>
        <p className="mt-4 text-xs text-neutral-600">
          {allCandidatesBlocked
            ? 'Refresh GRN candidates to reconcile this row or re-query Oracle before taking manual action.'
            : 'Pick one or more GRNs in the form below, then save & validate.'}
        </p>
      </section>
    </BucketShell>
  );
}
